/**
 * Bantoa Recommendations Web Component
 *
 * Recupera raccomandazioni prodotto da Bantoa (also-bought, similar, for-you, cross-ai, best-seller)
 * e le renderizza usando le product-card native del tema Shopify.
 *
 * Usa il Data Mode di Bantoa per ottenere gli SKU dei prodotti raccomandati,
 * poi recupera i prodotti completi da Shopify tramite la Search API.
 */

class BantoaReco extends HTMLElement {
  constructor() {
    super();
    this.products = [];
    this.bantoaProducts = []; // Dati originali Bantoa (con trackingurl)
  }

  connectedCallback() {
    console.log('BantoaReco: connectedCallback', {
      sku: this.dataset.sku,
      type: this.dataset.type,
      bnt_r_ai: !!window.bnt_r_ai
    });

    const handleBantoaLoaded = () => {
      console.log('BantoaReco: Evento mc:bantoa:loaded ricevuto, avvio init');
      window.removeEventListener('mc:bantoa:loaded', handleBantoaLoaded);
      setTimeout(() => this.init(), 100);
    };

    window.addEventListener('mc:bantoa:loaded', handleBantoaLoaded, { once: true });

    const tryStartIfBantoaReady = (reason) => {
      if (this._initialized) return;
      if (typeof window.bnt_r_ai !== 'function') return;
      window.removeEventListener('mc:bantoa:loaded', handleBantoaLoaded);
      console.log('BantoaReco: avvio init (' + reason + ')');
      this.init();
    };

    // mc:bantoa:loaded può essere già stato emesso prima del mount; su Safari/iOS bnt_r_ai spesso arriva nel tick successivo
    queueMicrotask(() => tryStartIfBantoaReady('microtask'));
    setTimeout(() => tryStartIfBantoaReady('0ms'), 0);
    setTimeout(() => tryStartIfBantoaReady('100ms'), 100);
    setTimeout(() => tryStartIfBantoaReady('500ms'), 500);

    setTimeout(() => tryStartIfBantoaReady('1.5s fallback'), 1500);

    setTimeout(() => {
      window.removeEventListener('mc:bantoa:loaded', handleBantoaLoaded);
      tryStartIfBantoaReady('15s timeout');
    }, 15000);
  }

  async init() {
    // Evita doppia inizializzazione
    if (this._initialized) {
      console.log('BantoaReco: già inizializzato, skip');
      return;
    }
    this._initialized = true;
    console.log('BantoaReco: init() chiamato');

    const sku = this.dataset.sku;
    const type = this.dataset.type || 'similar';
    const position = this.dataset.position || 'pdp-similar';
    const maxResults = parseInt(this.dataset.maxResults) || 12;
    const minResults = parseInt(this.dataset.minResults) || 2;
    const department = this.dataset.department || '';
    const country = this.dataset.country || '';
    const language = this.dataset.language || '';
    const fallbackToBestSeller = this.dataset.fallbackToBestSeller !== 'false';
    const fallbackMinProducts = parseInt(this.dataset.fallbackMinProducts) || 4;

    console.log('BantoaReco: Parametri', { sku, type, position, maxResults, minResults, department, country, language, fallbackToBestSeller, fallbackMinProducts });

    // Per best-seller non serve lo SKU
    if (!sku && type !== 'best-seller') {
      console.warn('BantoaReco: SKU non fornito');
      this.hideSection();
      return;
    }

    try {
      // Chiama Bantoa in data mode
      console.log('BantoaReco: Chiamata API Bantoa...');
      const bantoaData = await this.fetchBantoaRecommendations({
        sku,
        type,
        position,
        maxResults,
        minResults,
        department,
        country,
        language,
        fallbackToBestSeller,
        fallbackMinProducts
      });

      console.log('BantoaReco: Risposta Bantoa', bantoaData);

      if (!bantoaData?.data?.products?.length) {
        console.log('BantoaReco: Nessun prodotto trovato da Bantoa');
        this.hideSection();
        return;
      }

      // Salva i dati originali Bantoa (con trackingurl)
      this.bantoaProducts = bantoaData.data.products;
      console.log('BantoaReco: Prodotti Bantoa', this.bantoaProducts.length, this.bantoaProducts.map(p => ({ code: p.code, directurl: p.directurl })));

      if (this.bantoaProducts.length < minResults) {
        console.log('BantoaReco: Prodotti insufficienti', this.bantoaProducts.length, '<', minResults);
        this.hideSection();
        return;
      }

      // Recupera i prodotti Shopify tramite directurl o SKU
      console.log('BantoaReco: Recupero prodotti Shopify...');
      const shopifyProducts = await this.fetchShopifyProducts(this.bantoaProducts);
      console.log('BantoaReco: Prodotti Shopify trovati', shopifyProducts.length, shopifyProducts);

      if (shopifyProducts.length < minResults) {
        console.log('BantoaReco: Prodotti Shopify insufficienti', shopifyProducts.length, '<', minResults);
        this.hideSection();
        return;
      }

      // Ordina i prodotti Shopify secondo l'ordine di Bantoa
      const skus = this.bantoaProducts.map(p => p.code || p.sku).filter(Boolean);
      this.products = this.sortByBantoaOrder(shopifyProducts, skus);

      // Renderizza il carosello
      console.log('BantoaReco: Rendering carosello con', this.products.length, 'prodotti');
      await this.render();

      // Mostra la sezione
      this.showSection();
      console.log('BantoaReco: Sezione mostrata');

    } catch (error) {
      console.error('BantoaReco: Errore', error);
      this.hideSection();
    }
  }

  async fetchBantoaRecommendations({ sku, type, position, maxResults, minResults, department, country, language, fallbackToBestSeller, fallbackMinProducts }) {
    const params = {
      type: type,
      max_results: maxResults,
      min_results: minResults,
      position: position
    };

    // Aggiungi SKU - per Data Mode usa sempre "product" (anche per similar)
    // Normalizza lo SKU: lowercase e rimuovi l'ultimo carattere (indica taglia)
    if (sku && type !== 'best-seller') {
      params.product = sku.slice(0, -1).toLowerCase();
    }

    // Aggiungi department se specificato
    if (department) {
      params.department = department;
    }

    if (country) {
      params.country = country;
    }

    if (language) {
      params.language = language;
    }

    // Aggiungi parametri fallback
    if (fallbackToBestSeller) {
      params.fallbackToBestSeller = true;
      params.fallbackMinProducts = fallbackMinProducts;
    }

    console.log('BantoaReco: Parametri API Bantoa', params);

    const retryDelays = [0, 300, 700, 1200];

    for (let attempt = 0; attempt < retryDelays.length; attempt++) {
      if (retryDelays[attempt] > 0) {
        await this.wait(retryDelays[attempt]);
      }

      try {
        const data = await window.bnt_r_ai('reco', 'data', params);
        console.log('BantoaReco: Risposta API', data);
        if (data?.data?.products?.length) {
          return data;
        }
      } catch (err) {
        console.error('BantoaReco: Errore chiamata bnt_r_ai', err);
      }

      console.log('BantoaReco: risposta Bantoa non pronta, retry', attempt + 1, '/', retryDelays.length);
    }

    return null;
  }

  wait(delay) {
    return new Promise(resolve => setTimeout(resolve, delay));
  }

  async fetchShopifyProducts(bantoaProducts) {
    const products = [];

    // Recupera i prodotti in batch per non sovraccaricare
    const batchSize = 5;
    for (let i = 0; i < bantoaProducts.length; i += batchSize) {
      const batch = bantoaProducts.slice(i, i + batchSize);
      const batchResults = await Promise.all(
        batch.map(bp => this.fetchProductFromBantoa(bp))
      );
      products.push(...batchResults.filter(Boolean));
    }

    return products;
  }

  getLocalePrefix() {
    const pathMatch = window.location.pathname.match(/^(\/[a-z]{2}(?:-[a-z]{2})?)\//i);
    return pathMatch ? pathMatch[1] : '';
  }

  async fetchProductJson(urlOrHandle) {
    const localePrefix = this.getLocalePrefix();
    let url;
    if (urlOrHandle.startsWith('/')) {
      const path = localePrefix && !urlOrHandle.startsWith(`${localePrefix}/`)
        ? `${localePrefix}${urlOrHandle}`
        : urlOrHandle;
      url = path.endsWith('.js') ? path : `${path}.js`;
    } else {
      url = `${localePrefix}/products/${urlOrHandle}.js`;
    }
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) return null;
    return response.json();
  }

  async fetchProductFromBantoa(bantoaProduct) {
    const sku = bantoaProduct.code || bantoaProduct.sku || '';
    const directUrl = bantoaProduct.directurl || '';

    console.log('BantoaReco: Cerco prodotto', { sku, directUrl });

    // Metodo 1: Prova a usare directurl per estrarre l'handle
    if (directUrl) {
      const handle = this.extractHandleFromUrl(directUrl);
      if (handle) {
        try {
          const product = await this.fetchProductJson(handle);
          if (product) {
            console.log('BantoaReco: Trovato prodotto via directurl', product.handle);
            return product;
          }
        } catch (e) {
          console.log('BantoaReco: directurl non valido', handle);
        }
      }
    }

    // Metodo 2: Cerca per SKU
    return this.fetchProductBySku(sku);
  }

  extractHandleFromUrl(url) {
    try {
      // URL come "/it-it/products/nome-prodotto_tipo_colore_sku" o "https://domain.com/products/handle"
      const match = url.match(/\/products\/([^/?#]+)/);
      if (match && match[1]) {
        return match[1];
      }
    } catch (e) {}
    return null;
  }

  async fetchProductBySku(sku) {
    if (!sku) return null;

    try {
      // Bantoa restituisce SKU nel formato "sab0069983001s024-bianco-lana"
      // Il formato Bantoa: {prefisso}{codice_prodotto}{codice_colore}-{nome_colore}
      // Esempio: sab0069983001s024-bianco-lana -> codice prodotto è "sab0069983001"
      // Lo SKU Shopify potrebbe avere un codice colore diverso: sab0069983001sc68

      const skuFull = sku.split('-')[0]; // es: "sab0069983001s024"
      // Estrai solo il codice prodotto (senza il codice colore finale - ultimi 4 caratteri)
      const skuProduct = skuFull.slice(0, -4); // es: "sab0069983001"

      console.log('BantoaReco: Cerco per SKU', { sku, skuFull, skuProduct });

      const localePrefix = this.getLocalePrefix();
      // Cerca usando il codice prodotto (senza codice colore)
      const searchUrl = `${localePrefix}/search/suggest.json?q=${encodeURIComponent(skuProduct)}&resources[type]=product&resources[limit]=10&resources[options][fields]=sku,variants.sku,handle,title`;
      const response = await fetch(searchUrl);
      const data = await response.json();

      if (data.resources?.results?.products?.length) {
        console.log('BantoaReco: Risultati search per', skuProduct, data.resources.results.products.map(p => p.handle));

        // Cerca il prodotto il cui handle contiene il codice prodotto
        for (const productData of data.resources.results.products) {
          const handle = productData.handle?.toLowerCase() || '';
          const url = productData.url?.toLowerCase() || '';
          if (handle.includes(skuProduct) || url.includes(skuProduct)) {
            const product = await this.fetchProductJson(productData.url);
            if (product) {
              console.log('BantoaReco: Trovato prodotto via search', product.handle);
              return product;
            }
          }
        }

        // Se non trova match esatto con codice prodotto, prova con SKU completo
        for (const productData of data.resources.results.products) {
          const handle = productData.handle?.toLowerCase() || '';
          if (handle.includes(skuFull)) {
            const product = await this.fetchProductJson(productData.url);
            if (product) {
              console.log('BantoaReco: Trovato prodotto via SKU completo', product.handle);
              return product;
            }
          }
        }

        // Prova il primo risultato se contiene almeno parte del codice
        const productData = data.resources.results.products[0];
        const product = await this.fetchProductJson(productData.url);
        if (product) {
          console.log('BantoaReco: Uso primo risultato', product.handle);
          return product;
        }
      }

      console.warn('BantoaReco: Prodotto non trovato per SKU', sku);
      return null;
    } catch (error) {
      console.warn('BantoaReco: Errore ricerca prodotto per SKU', sku, error);
      return null;
    }
  }

  sortByBantoaOrder(shopifyProducts, skus) {
    // Crea una mappa SKU base -> indice per ordinamento
    // Gli SKU Bantoa sono nel formato "sab0069983001s024-bianco-lana"
    const skuOrder = new Map(skus.map((sku, index) => [sku.split('-')[0].toLowerCase(), index]));

    return shopifyProducts.sort((a, b) => {
      // Estrai lo SKU base dall'handle Shopify (ultimo segmento dopo _)
      const handleA = a.handle?.toLowerCase() || '';
      const handleB = b.handle?.toLowerCase() || '';

      // Cerca quale SKU base corrisponde a questo prodotto
      let orderA = 999, orderB = 999;
      for (const [skuBase, index] of skuOrder) {
        if (handleA.includes(skuBase)) orderA = Math.min(orderA, index);
        if (handleB.includes(skuBase)) orderB = Math.min(orderB, index);
      }

      return orderA - orderB;
    });
  }

  getProductSku(product) {
    // Prova a recuperare lo SKU dalla variante selezionata o dalla prima disponibile
    if (product.selected_or_first_available_variant?.sku) {
      return product.selected_or_first_available_variant.sku;
    }
    if (product.variants?.[0]?.sku) {
      return product.variants[0].sku;
    }
    return product.handle;
  }

  // Trova il trackingurl di Bantoa per un prodotto Shopify
  getBantoaTrackingUrl(shopifyProduct) {
    const shopifyHandle = shopifyProduct.handle?.toLowerCase() || '';

    for (const bp of this.bantoaProducts) {
      const bantoaCode = (bp.code || bp.sku || '').toLowerCase();
      // Estrai la parte base dello SKU Bantoa (prima del trattino con colore)
      const bantoaSkuBase = bantoaCode.split('-')[0];

      // L'handle Shopify contiene lo SKU base (es: blusa-plissettata_camicia-ml_ultrablack_sab0069983001sc68)
      if (bantoaSkuBase && shopifyHandle.includes(bantoaSkuBase)) {
        return bp.trackingurl;
      }
    }
    return null;
  }

  async render() {
    const sectionId = this.dataset.sectionId;
    const slidesDesktop = this.dataset.slidesDesktop || 4;
    const slidesMobile = this.dataset.slidesMobile || 2;
    const gap = this.dataset.gap || 1;
    const hasLoop = this.hasAttribute('data-loop');

    // Costruisci il carosello con mc-swiper
    // Usa selettori unici per le frecce per evitare conflitti con altri swiper nella pagina
    const navNextSelector = `#bantoa-nav-next-${sectionId}`;
    const navPrevSelector = `#bantoa-nav-prev-${sectionId}`;

    let html = `
      <mc-swiper
        id="bantoa-reco-swiper-${sectionId}"
        class="swiper"
        data-slides="${slidesMobile}"
        data-navigation='{"nextEl": "${navNextSelector}", "prevEl": "${navPrevSelector}"}'
        data-space-between="${gap}"
        ${hasLoop ? 'data-loop' : ''}
      >
        <mc-swiper-breakpoint
          data-breakpoint="1024"
          data-slides="${slidesDesktop}"
        ></mc-swiper-breakpoint>
        <div class="swiper-wrapper">
    `;

    const cardHtmlList = await Promise.all(this.products.map(async product => {
      const trackingUrl = this.getBantoaTrackingUrl(product);
      const cardHtml = await this.fetchProductCard(product.handle, trackingUrl);
      return cardHtml ? `<div class="swiper-slide">${cardHtml}</div>` : '';
    }));
    const renderedCards = cardHtmlList.filter(Boolean);
    html += renderedCards.join('');

    if (renderedCards.length < (parseInt(this.dataset.minResults) || 2)) {
      console.log('BantoaReco: Card insufficienti dopo render', renderedCards.length);
      this.hideSection();
      return;
    }

    html += `
        </div>
        <div id="bantoa-nav-next-${sectionId}" class="swiper-button-next"></div>
        <div id="bantoa-nav-prev-${sectionId}" class="swiper-button-prev"></div>
      </mc-swiper>
    `;

    this.innerHTML = html;

    // Carica i componenti necessari
    if (window.loadComponent) {
      await loadComponent('./components/mc-swiper.js');
    }
    if (window.themeComponent && window.theme?.components) {
      themeComponent(theme.components['product-card']);
      themeComponent(theme.components['stoq-btn']);
      loadComponent('./wishlist/teddy-wishlist-btn.js');
    }

    this.refreshOmnibusBlocks();
  }

  refreshOmnibusBlocks() {
    if (typeof window.updateOmnibusForVariant !== 'function') return;

    this.querySelectorAll('[data-omnibus-block]').forEach((block) => {
      const variantId = block.dataset.variantId;
      if (!variantId || block.dataset.omnibusFetched === '1') return;
      block.dataset.omnibusFetched = '1';
      const scope = block.closest('[data-omnibus-slot]') || block.parentElement;
      window.updateOmnibusForVariant(variantId, scope);
    });
  }

  async fetchProductCard(handle, trackingUrl) {
    try {
      const localePrefix = this.getLocalePrefix();
      const response = await fetch(`${localePrefix}/products/${handle}?section_id=bantoa-reco-card`, {
        credentials: 'same-origin'
      });
      if (response.ok) {
        let html = await response.text();
        // Se c'è un trackingUrl Bantoa, sostituisci i link del prodotto
        if (trackingUrl) {
          html = this.replaceProductLinks(html, handle, trackingUrl);
        }
        return html;
      }

      // Fallback: genera una card semplice
      const product = this.products.find(p => p.handle === handle);
      if (product) {
        return this.generateFallbackCard(product, trackingUrl);
      }

      return '';
    } catch (error) {
      console.warn('BantoaReco: Errore caricamento card per', handle);
      return '';
    }
  }

  // Sostituisce i link del prodotto con il trackingUrl di Bantoa
  replaceProductLinks(html, handle, trackingUrl) {
    // Sostituisce href="/products/handle" e href="/it-it/products/handle" etc
    const productUrlPattern = new RegExp(`href="[^"]*\\/products\\/${handle}[^"]*"`, 'gi');
    return html.replace(productUrlPattern, `href="${trackingUrl}"`);
  }

  generateFallbackCard(product, trackingUrl) {
    const variant = product.variants?.[0] || {};
    const variantId = variant.id || '';
    const price = variant.price ? (variant.price / 100).toFixed(2) : '';
    const comparePrice = variant.compare_at_price ? (variant.compare_at_price / 100).toFixed(2) : '';
    const image = product.featured_image || product.images?.[0];
    const productUrl = trackingUrl || `/products/${product.handle}`;
    const isPolandMarket = String(this.dataset.country || '').toUpperCase() === 'PL';
    const omnibusBlock = isPolandMarket && variantId
      ? `<div class="bantoa-omnibus-slot" data-omnibus-slot><div class="product-omnibus-price d-none" data-omnibus-block data-variant-id="${variantId}"><span class="product-omnibus-price__line d-none" data-omnibus-regular-line><span class="product-omnibus-price__amount product-omnibus-price__amount--struck" data-omnibus-regular-price></span><span class="product-omnibus-price__discount" data-omnibus-regular-discount></span></span><span class="product-omnibus-price__line" data-omnibus-lowest-line><span class="product-omnibus-price__amount product-omnibus-price__amount--struck" data-omnibus-price></span><span data-omnibus-discount-wrapper class="d-none"><span class="product-omnibus-price__discount" data-omnibus-discount></span></span></span></div></div>`
      : '';

    return `
      <product-card class="product-card">
        <a href="${productUrl}" class="product-card-link">
          <div class="product-card-image">
            ${image ? `<img src="${image}" alt="${product.title}" loading="lazy">` : ''}
          </div>
          <div class="product-card-info">
            <h3 class="product-card-title">${product.title}</h3>
            <div class="product-card-price">
              ${comparePrice && comparePrice !== price ? `<s>${comparePrice} €</s>` : ''}
              <span>${price} €</span>
            </div>
            ${omnibusBlock}
          </div>
        </a>
      </product-card>
    `;
  }

  hideSection() {
    const section = document.getElementById(`bantoa-reco-${this.dataset.sectionId}`);
    if (section) {
      section.style.display = 'none';
    }
    this.innerHTML = '';
  }

  showSection() {
    const section = document.getElementById(`bantoa-reco-${this.dataset.sectionId}`);
    if (section) {
      section.style.display = '';
    }
  }
}

if (!customElements.get('bantoa-reco')) {
  customElements.define('bantoa-reco', BantoaReco);
}
